import { useDebounceEffect } from 'ahooks';
import { Form } from 'antd';
import { snakeCase } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLatest } from 'hooks/use-latest';
import { removeLineBreaksFromString } from 'utils/html-sanitizer';
import { MultiLineTextField, TextField } from '../fields';
import {
  isOptional,
  TextFieldFilter,
  TextFieldFilterOperationType,
  TextFieldFilterOperationTypes,
} from '../filters/text';
import { Select, TextInput } from './components';
import { CyWrapper } from './wrappers/cy-wrapper';

interface Props {
  filter: TextFieldFilter;
  onChange: (filter: TextFieldFilter) => void;
  getFilterOperationTypeOptions?: (
    field: TextField | MultiLineTextField,
  ) => TextFieldFilter[];
  getOperationOptionRenderer?: (
    type: TextFieldFilterOperationType,
  ) => null | JSX.Element;
}

const defaultOptions = (
  field: TextField | MultiLineTextField,
): TextFieldFilter[] =>
  TextFieldFilterOperationTypes.filter(
    (x) => !field.metaData?.required || !isOptional(x),
  ).map(
    (type) =>
      ({
        field,
        type,
      } as any as TextFieldFilter),
  );

export const TextCriteria: FC<Props> = ({
  filter,
  onChange,
  getFilterOperationTypeOptions = defaultOptions,
  getOperationOptionRenderer,
}) => {
  const [text, setText] = useState<string | undefined>((filter as any).value);
  const filterText = (filter as any).value as string | undefined;
  const latestOnChange = useLatest(onChange);

  useEffect(() => {
    setText(filterText);
  }, [filterText, setText]);
  const filterRef = useRef(filter);

  useEffect(() => {
    filterRef.current = filter;
  }, [filterRef, filter]);
  useDebounceEffect(
    () => {
      latestOnChange({
        ...filterRef.current,
        value: text,
      } as any);
    },
    [filterRef, text, latestOnChange],
    {
      wait: 300,
      trailing: true,
    },
  );

  return (
    <>
      <Form.Item>
        <Select
          disabled={filter == null}
          value={filter?.type}
          onChange={(selected) =>
            onChange({
              ...filter,
              type: selected,
            } as any)
          }
        >
          {(filter?.field == null
            ? []
            : getFilterOperationTypeOptions(filter.field)
          ).map((option) => (
            <Select.Option key={option.type} value={option.type}>
              <CyWrapper filter={filter} option={option}>
                {getOperationOptionRenderer?.(option.type) ?? (
                  <FormattedMessage
                    id={`task_list.filters.${snakeCase(
                      TextFieldFilterOperationType[option.type],
                    )}`}
                  />
                )}
              </CyWrapper>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {filter != null &&
        (filter.type === TextFieldFilterOperationType.Contains ||
          filter.type === TextFieldFilterOperationType.DoesNotContain) && (
          <Form.Item>
            <TextInput
              data-cy={`select.fields.input.${
                filter?.field?.fieldLabel ?? '__new__'
              }`}
              value={text}
              onChange={(event) =>
                setText(removeLineBreaksFromString(event.target.value))
              }
            />
          </Form.Item>
        )}
    </>
  );
};
